// ─────────────────────────────────────────────────────────────────────────────
// formService.js  —  Form şablonu listesi (Forms/FormsListPage)
//
// FLAG: USE_MOCK_DATA = true → localStorage mock  |  false → gerçek API
//
// API endpoint mapping:
//   getAll()        → GET  /api/v1/inspection/scopes → types per scope (flatten)
//   remove(id)      → DELETE /api/v1/inspection/types/{id}  (soft delete)
// Şablonu yaratma / düzenleme (type, category, question) Forms/FormEditorPage'de
// inspectionAdminService üzerinden yapılır; bu servis yalnızca liste tarafı içindir.
// ─────────────────────────────────────────────────────────────────────────────
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { normalizeScopeRow } from './sahaInspectionService';
import { normalizeItem } from './inspectionAdminService';

export const USE_MOCK_DATA = false; // FLAG: ✅ API bağlantısı aktif
const INSPECTION_API = `${JAVA_BACKEND_API_URL}/inspection`;

const REGISTRY_KEY  = "fds_forms_registry";
const SCHEMA_VERSION = "v3"; // Değiştiğinde localStorage temizlenir
const VERSION_KEY    = "fds_forms_version";
const dataKey = (id) => `fds_form_data_${id}`;

// ── Seed registry (ilk açılışta gösterilecek örnek formlar) ─────────────────
const SEED_REGISTRY = [
  {
    id: 1,
    name: "ISG Stant Denetim Formu 2026",
    type: "denetim",
    inspectionLevelId: 1,   // API: inspectionLevelId (1=Denetim, 2=Uygunsuzluk, 3=Kapalı DÖF)
    scope: "stand",
    shortName: "ISG",
    isStarred: true,
    status: "yayinda",
    questionCount: 17,
    categoryCount: 4,
    createdAt: "2026-03-15T09:00:00.000Z",
    updatedAt: "2026-04-07T10:30:00.000Z",
  },
  {
    id: 2,
    name: "Elektrik Güvenlik Denetim Formu",
    type: "denetim",
    inspectionLevelId: 1,
    scope: "stand",
    shortName: "ELK",
    isStarred: false,
    status: "yayinda",
    questionCount: 12,
    categoryCount: 3,
    createdAt: "2026-03-20T10:00:00.000Z",
    updatedAt: "2026-04-05T14:00:00.000Z",
  },
  {
    id: 3,
    name: "Uygunsuzluk Kayıt Formu v2",
    type: "uygunsuzluk",
    inspectionLevelId: 2,
    scope: "stand",
    shortName: "UYG",
    isStarred: true,
    status: "yayinda",
    questionCount: 8,
    categoryCount: 2,
    createdAt: "2026-03-25T11:00:00.000Z",
    updatedAt: "2026-04-06T16:00:00.000Z",
  },
  {
    id: 4,
    name: "DÖF Kapama Formu",
    type: "dof",
    inspectionLevelId: 3,
    scope: "stand",
    shortName: "DOF",
    isStarred: false,
    status: "taslak",
    questionCount: 6,
    categoryCount: 2,
    createdAt: "2026-04-01T08:00:00.000Z",
    updatedAt: "2026-04-07T09:00:00.000Z",
  },
  {
    id: 5,
    name: "Yangın Güvenliği Kontrol Formu",
    type: "denetim",
    inspectionLevelId: 1,
    scope: "fuar_alani",
    shortName: "YGN",
    isStarred: false,
    status: "taslak",
    questionCount: 10,
    categoryCount: 2,
    createdAt: "2026-04-03T11:00:00.000Z",
    updatedAt: "2026-04-07T11:00:00.000Z",
  },
];

// ── Yardımcılar ──────────────────────────────────────────────────────────────
function loadRegistry() {
  try {
    // Versiyon kontrolü — schema değişince eski cache temizlenir
    const storedVersion = localStorage.getItem(VERSION_KEY);
    if (storedVersion !== SCHEMA_VERSION) {
      const oldKeys = Object.keys(localStorage).filter(
        (k) => k.startsWith("fds_form")
      );
      oldKeys.forEach((k) => localStorage.removeItem(k));
      localStorage.setItem(REGISTRY_KEY, JSON.stringify(SEED_REGISTRY));
      localStorage.setItem(VERSION_KEY, SCHEMA_VERSION);
      return SEED_REGISTRY;
    }
    const raw = localStorage.getItem(REGISTRY_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  // İlk açılış: seed'i kaydet
  localStorage.setItem(REGISTRY_KEY, JSON.stringify(SEED_REGISTRY));
  localStorage.setItem(VERSION_KEY, SCHEMA_VERSION);
  return SEED_REGISTRY;
}

// ── UI Tercihleri (API'de karşılığı yok: yıldız, durum) ──────────────────────
// API modunda registry boş olacağından ayrı bir prefs haritası kullanılır
const PREFS_KEY = "fds_form_prefs";
function loadPrefs() {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY) || '{}'); } catch { return {}; }
}
function savePrefs(prefs) { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); }

function mergePrefs(forms) {
  const prefs = loadPrefs();
  return forms.map((f) => {
    const p = prefs[String(f.id)];
    return p ? { ...f, ...p } : f;
  });
}

function saveRegistry(registry) {
  localStorage.setItem(REGISTRY_KEY, JSON.stringify(registry));
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Tüm form şablonlarını listele */
export async function getAll() {
  if (USE_MOCK_DATA) return loadRegistry();
  // API: Tüm scope'ları al → her scope için type'ları al → flatten
  const scopesRes = await axios.get(
    `${INSPECTION_API}/scopes?languageId=1`,
    { headers: getAuthHeader() }
  );
  const scopes = (scopesRes.data || []).map(normalizeScopeRow);
  const allTypes = await Promise.all(
    scopes.map((scope) =>
      axios.get(
        `${INSPECTION_API}/types?parentId=${scope.inspection_scope_id}&languageId=1`,
        { headers: getAuthHeader() }
      ).then((r) =>
        (r.data || []).map((raw) => {
          const t = normalizeItem('types', raw); // backend raw array → obje
          return {
            id:               t.inspection_type_id,
            name:             t.inspection_type_name,
            type:             'denetim',
            inspectionLevelId: 1,
            scope:            scope.target_entity || scope.inspection_scope_name || '',
            scopeId:          scope.inspection_scope_id,
            shortName:        t.inspection_type_short || '',
            isStarred:        false,
            status:           'yayinda',
            questionCount:    0,
            categoryCount:    0,
            createdAt:        null,
            updatedAt:        null,
          };
        })
      )
    )
  );
  return mergePrefs(allTypes.flat());
}

/** Formu sil (soft delete) */
export async function remove(id) {
  if (USE_MOCK_DATA) {
    const numId = Number(id);
    saveRegistry(loadRegistry().filter((f) => f.id !== numId));
    localStorage.removeItem(dataKey(numId));
    return;
  }
  // DELETE /api/v1/inspection/types/{id}  (soft delete — is_deleted=1)
  await axios.delete(`${INSPECTION_API}/types/${id}`, { headers: getAuthHeader() });
}

/** Yıldız toggle — API karşılığı yok, tercihler localStorage'da tutulur */
export async function toggleStar(id) {
  if (USE_MOCK_DATA) {
    const numId = Number(id);
    const registry = loadRegistry();
    const idx = registry.findIndex((f) => f.id === numId);
    if (idx === -1) return;
    registry[idx].isStarred = !registry[idx].isStarred;
    saveRegistry(registry);
    return;
  }
  const prefs = loadPrefs();
  const key = String(id);
  prefs[key] = { ...prefs[key], isStarred: !prefs[key]?.isStarred };
  savePrefs(prefs);
}

/** Durum değiştir — API karşılığı yok, tercihler localStorage'da tutulur */
export async function toggleStatus(id) {
  if (USE_MOCK_DATA) {
    const numId = Number(id);
    const registry = loadRegistry();
    const idx = registry.findIndex((f) => f.id === numId);
    if (idx === -1) return;
    registry[idx].status    = registry[idx].status === "yayinda" ? "taslak" : "yayinda";
    registry[idx].updatedAt = new Date().toISOString();
    saveRegistry(registry);
    return;
  }
  const prefs = loadPrefs();
  const key = String(id);
  const cur = prefs[key]?.status || 'taslak';
  prefs[key] = { ...prefs[key], status: cur === 'yayinda' ? 'taslak' : 'yayinda', updatedAt: new Date().toISOString() };
  savePrefs(prefs);
}
